import type { ContractRole, ContractType } from '@paid-ui/enums/contract';

import { useQuery } from '@tanstack/react-query';

import { request } from '@paid-ui/utils/axios';

import { useInvitationSearchParams } from '../hooks/search-params';

type GetSignUpInvitationForContractResponse = {
  data: {
    email: string;
    contractType: ContractType | null;
    firstName: string;
    lastName: string;
    signupRequired: boolean;
    role: ContractRole;
    groupId?: string;
  };
} | null;

export const useGetSignUpInvitationForContract = () => {
  const { email, contractType, contractId, token } = useInvitationSearchParams();

  return useQuery({
    retry: false,
    enabled: Boolean(contractId && email && token),
    queryKey: ['GetSignUpInvitationWithContract', email, contractType, contractId, token],
    queryFn: async ({ signal }) => {
      const { data } = await request.get<GetSignUpInvitationForContractResponse>(
        `/users/complete-signup`,
        {
          signal,
          params: {
            contractId,
            email,
            token,
          },
        },
      );

      return data?.data
        ? {
            ...data.data,
            email,
            contractType,
          }
        : null;
    },
  });
};

type GetSignUpInvitationForGroupResponse = {
  data: {
    email: string;
    firstName: string;
    lastName: string;
    signupRequired: boolean;
    groupId: string;
  };
} | null;

export const useGetSignUpInvitationForGroup = () => {
  const { email, contractId, groupId, token } = useInvitationSearchParams();

  return useQuery({
    retry: false,
    enabled: !contractId && Boolean(groupId && email && token),
    queryKey: ['GetSignUpInvitationWithGroup', email, groupId, token],
    queryFn: async ({ signal }) => {
      const { data } = await request.post<GetSignUpInvitationForGroupResponse>(
        `/users/complete-signup/group/info`,
        {
          groupId,
          email,
          token,
        },
        {
          signal,
        },
      );

      return data?.data
        ? {
            ...data.data,
            email,
          }
        : null;
    },
  });
};
